import { useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { docsGuideLink } from '@/components/layout/HelpMenu';
import { useOptionalToast } from '@/contexts/ToastContext';
import { useFlag } from '@/hooks/useFeatureFlags';
import { useAccountVersioning, useEnableVersioning } from '@/hooks/useResourceVersions';

/**
 * Account settings — "Publishing & versions" (task 725).
 *
 * This is the *only* place the feature is discoverable while an Account has not
 * adopted it. Everything the section says is shaped by two facts:
 *
 *   1. **Enabling is permanent.** Un-versioning resources whose published
 *      versions are serving in environments has no safe meaning, so there is no
 *      disable control anywhere — not disabled, not hidden: it does not exist.
 *      The confirmation dialog restates that before anything happens.
 *   2. **Enabling is instant and safe.** It mints no versions and changes no
 *      behaviour; every resource stays live-edit until its own first publish.
 *      Saying so is what keeps the switch from reading as a migration.
 *
 * The "Learn more" link points at the user guide from
 * `project/tasks/726-docs-resource-versioning-documentation.md`.
 */

/**
 * User-guide slug for the publish lifecycle. The docs task and this link have
 * to name the same page; if the guide ships under a different slug, this is the
 * single line that moves.
 */
const VERSIONING_GUIDE_SLUG = 'innovate-publishing-and-versions';

export function PublishingVersionsSection() {
  const featureEnabled = useFlag('innovate.fe.resource-versioning');
  const { data: settings, isLoading } = useAccountVersioning();
  const enableVersioning = useEnableVersioning();
  const toast = useOptionalToast();

  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!featureEnabled) return null;

  const adopted = settings?.versioningEnabled === true;

  const confirmEnable = () => {
    enableVersioning.mutate(undefined, {
      onSuccess: () => {
        setConfirmOpen(false);
        toast?.success(
          'Publishing & versions enabled',
          'Nothing changed yet — every resource stays live-edit until you publish it.'
        );
      },
      onError: () => {
        setConfirmOpen(false);
        toast?.error(
          'Could not enable Publishing & versions',
          'Your account was not changed. Please try again.'
        );
      },
    });
  };

  return (
    <section
      data-testid="publishing-versions-section"
      className="mt-6 rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Publishing &amp; versions
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Published versions are frozen; edits always land on a new draft version. Turning this on
            adopts the practice for everything in the account at once.
          </p>
        </div>

        {!isLoading && !adopted && (
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            data-testid="versioning-enable"
            className="flex-shrink-0 rounded-md border border-transparent bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          >
            Enable
          </button>
        )}
      </div>

      {isLoading && (
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400" data-testid="versioning-loading">
          Loading your publishing settings…
        </p>
      )}

      {!isLoading && !adopted && (
        <div className="mt-4" data-testid="versioning-off-state">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Right now every change you make takes effect immediately. With publishing on, you choose
            when a version of an AI Service becomes permanent — and everything that was published
            keeps running exactly as it was while you keep editing.
          </p>
          <p className="mt-2 flex items-start gap-1.5 text-sm text-amber-700 dark:text-amber-300">
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
            Enabling this is permanent — it cannot be turned off again.
          </p>
          <a
            href={docsGuideLink(VERSIONING_GUIDE_SLUG)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="versioning-learn-more"
            className="mt-3 inline-block text-sm font-medium text-indigo-600 hover:underline dark:text-indigo-400"
          >
            Learn more
          </a>
        </div>
      )}

      {!isLoading && adopted && (
        <div className="mt-4" data-testid="versioning-on-state">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            <span className="font-medium text-gray-900 dark:text-white">Enabled.</span> Every AI
            Service, config, agent and prompt in this account can be published. Each one keeps
            editing live until you publish it for the first time.
          </p>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            This is permanent, so there is nothing to turn off here.
          </p>
          <a
            href={docsGuideLink(VERSIONING_GUIDE_SLUG)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="versioning-learn-more"
            className="mt-3 inline-block text-sm font-medium text-indigo-600 hover:underline dark:text-indigo-400"
          >
            Learn more
          </a>
        </div>
      )}

      {confirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="versioning-confirm-title"
          data-testid="versioning-confirm-dialog"
          onClick={(event) => {
            if (event.target === event.currentTarget && !enableVersioning.isPending) {
              setConfirmOpen(false);
            }
          }}
        >
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
            <div className="mb-4 flex items-start gap-3">
              <ExclamationTriangleIcon
                className="h-6 w-6 flex-shrink-0 text-amber-500"
                aria-hidden="true"
              />
              <h3
                id="versioning-confirm-title"
                className="text-lg font-medium text-gray-900 dark:text-white"
              >
                Enable Publishing &amp; versions?
              </h3>
            </div>

            <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
              <p>
                This adopts publishing for the whole account, and{' '}
                <span className="font-semibold">it cannot be turned off again.</span>
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Nothing changes today: no versions are created, and every resource keeps editing live
                until you publish it for the first time.
              </p>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                disabled={enableVersioning.isPending}
                data-testid="versioning-confirm-cancel"
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmEnable}
                disabled={enableVersioning.isPending}
                data-testid="versioning-confirm-submit"
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-50"
              >
                {enableVersioning.isPending ? 'Enabling…' : 'Enable permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default PublishingVersionsSection;
